import { connect } from "react-redux";

import type * as UDM from "@busbud/int-schemas/udm";

import { SeatSelectionDetailsMode } from "@app/constants/seat-selection-details-modes";
import { removeTimeOffset } from "@app/helpers/time";
import { getSegmentIdsPerLeg } from "@app/helpers/udm/get-segment-ids-per-leg";
import { getSeatById } from "@app/helpers/udm/vehicle";
import { getCartTicketChargesItems } from "@app/modules/checkout/store/v2/selectors/get-cart-ticket-charges-items";
import { filterTripCartItems } from "@app/modules/checkout/utils/cart-v2";
import {
  getCart,
  getLocale
} from "@app/modules/results/selectors/state-accessors";
import { getSearchedLegOperators } from "@app/modules/results/selectors/trips/get-trip-operators";
import type { CurrencyCode } from "@app/types/currency";
import type { NapiV2Cart, NapiV2TripItem } from "@app/types/napi-v2-cart-types";
import type { TicketCharges } from "@app/types/napi-v2-charges-types";
import type { ResultsReduxState } from "@app/types/results-redux-types";
import type { SearchInfo } from "@app/types/search-types";
import type { PassengerSeatSelectionInstance } from "@app/types/seat-selection-types";

import type { SeatPrices } from "../seat-price";
import type { Props as SeatSelectionContentProps } from "../seat-selection-content/seat-selection-content";
import type {
  Direction,
  SelectedSeatsInfo
} from "../seat-selector/seat-selector";

import { SeatSelectionContent } from "../seat-selection-content/seat-selection-content";
import { createPassenger } from "../seat-selector/seat-selector.utils";

interface OwnProps {
  passenger_index?: number;
  direction: "outbound" | "return";
}

const mapToProps = (
  currency: CurrencyCode,
  trip_id: string,
  search: SearchInfo,
  ticket_cart_charges: TicketCharges[],
  trip: NapiV2TripItem,
  operator: UDM.MappedOperator | undefined,
  direction: Direction,
  locale: string | null,
  passenger_index?: number
) => {
  const segment_ids_per_leg_matrix = getSegmentIdsPerLeg(trip.trip_legs);

  const direction_index = direction === "outbound" ? 0 : 1;

  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const first_segment_id = segment_ids_per_leg_matrix[direction_index][0];

  const first_segment = trip.segments.find(
    segment => segment.id === first_segment_id
  )!;

  const charges_per_direction = ticket_cart_charges.filter(
    charges => charges.segment_id === first_segment_id
  );

  const is_external = false;

  const vehicle = first_segment.vehicle as UDM.Bus;

  const prices = charges_per_direction.reduce(
    (acc, curr) => {
      const categories = { ...acc.categories };
      const subtotal = acc.subtotal + curr.amount;

      if (!categories[curr.passenger.category]) {
        categories[curr.passenger.category] = curr.amount;
      }

      return { categories, subtotal };
    },
    { categories: {}, subtotal: 0 } as SeatPrices
  );

  const departure_time = removeTimeOffset(
    first_segment.departure_time.timestamp
  );

  const operator_disclaimers =
    locale && first_segment.disclaimers
      ? first_segment.disclaimers[locale]
      : null;
  const contractor_name = first_segment.contractor?.name || null;

  const is_vehicle_loading_complete = Array.isArray(vehicle.layout);

  const has_selected_seats = trip.passengers.some(
    passenger => passenger.selected_seats && passenger.selected_seats.length > 0
  );

  let initial_passengers: PassengerSeatSelectionInstance[] | undefined;
  const initial_selected_seats: SelectedSeatsInfo = {};

  if (has_selected_seats) {
    const direction_seat =
      direction === "outbound"
        ? "outbound_selected_seat"
        : "return_selected_seat";

    initial_passengers = trip.passengers.map(passenger => {
      const seat = passenger.selected_seats?.find(
        seat => seat.segment_id === first_segment.id
      )!;

      const questions_obj =
        passenger.answers && passenger.answers.length > 0
          ? passenger.answers.reduce<Record<string, unknown>>((acc, curr) => {
              acc[curr.question_key] = curr.value;
              return acc;
            }, {})
          : {};

      const options: Partial<PassengerSeatSelectionInstance> = {
        passenger_category: passenger.category,
        [direction_seat]: seat?.seat_id,
        selected_seats: passenger.selected_seats!,
        questions: questions_obj
      };

      const seat_info = seat && getSeatById(vehicle, seat.seat_id);

      if (seat_info) {
        initial_selected_seats[seat.seat_id] = seat_info;
      }

      return createPassenger(options);
    });
  }

  const props: SeatSelectionContentProps = {
    contractor_name,
    currency,
    departure_time,
    is_external,
    mode: SeatSelectionDetailsMode.Drawer,
    // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    operator_disclaimers,
    operator_name: operator?.name || "",
    prices,
    search,
    segment_id: first_segment.id,
    trip_id,
    vehicle,
    is_vehicle_loading_complete,
    is_checkout: true,
    initial_passengers,
    initial_selected_seats,
    passenger_index
  };

  return props;
};

const mapStateToProps = (state: ResultsReduxState, own_props: OwnProps) => {
  const cart_info = getCart(state);
  // Async cart is only using Cart v2
  // At this point we always have a cart (seat selection doesn't show without one)
  const cart = cart_info!.cart as NapiV2Cart;

  const trip = filterTripCartItems(cart.items)[0];
  // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const trip_id = trip.id;
  const { currency } = cart.charges;
  const { search } = state;

  const ticket_cart_charges = getCartTicketChargesItems(state);
  const [operator] = getSearchedLegOperators(state, trip_id);

  const locale = getLocale(state);
  return mapToProps(
    currency,
    trip_id,
    search,
    ticket_cart_charges,
    // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    trip,
    operator,
    own_props.direction,
    locale,
    own_props.passenger_index
  );
};

const connector = connect(mapStateToProps);

export const SeatSelectionAsyncCartModal = connector(SeatSelectionContent);
